// https://leaflet-extras.github.io/leaflet-providers/preview/
// Os mapas base "ESRI*" vêm do ArcGIS Living Atlas (serviços raster públicos de
// server.arcgisonline.com). Uso livre mediante atribuição.
export enum Tiles {
  OpenStreetMap = "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  OpenStreetMapCAT = "https://tile.openstreetmap.bzh/ca/{z}/{x}/{y}.png",
  OpenTopoMap = "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
  ThunderforestOutdoors = "https://api.thunderforest.com/outdoors/{z}/{x}/{y}{r}.png?apikey={apikey}",
  ESRISatelite = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  ESRIFisico = "https://{s}.tile-cyclosm.openstreetmap.fr/cyclosm/{z}/{x}/{y}.png",
  ESRITopografico = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
  ESRIRelevo = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Shaded_Relief/MapServer/tile/{z}/{y}/{x}",
  ESRICinzaEscuro = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
}

export const DEFAULT_TILE = Tiles.OpenStreetMap;

/** Zoom máximo permitido pelo mapa. Deve casar com o maxZoom do MapContainer. */
export const MAP_MAX_ZOOM = 18;

const OSM_ATTRIBUTION = `&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors`;

const ESRI_ATTRIBUTION = `Tiles &copy; Esri &mdash; Fonte: Esri e a comunidade de usuários GIS`;
export interface TileConfig {
  /** Rótulo exibido no seletor de mapa base. */
  name: string;
  /**
   * Maior zoom em que o provedor tem tiles reais. Acima disso o Leaflet faz
   * upscale da última tile, em vez de esconder a camada.
   */
  maxNativeZoom: number;
  attribution: string;
}

export const TILE_CONFIG: Record<Tiles, TileConfig> = {
  [Tiles.OpenStreetMap]: {
    name: "OpenStreetMap",
    maxNativeZoom: 19,
    attribution: OSM_ATTRIBUTION,
  },
  [Tiles.OpenStreetMapCAT]: {
    name: "OpenStreetMap CAT",
    maxNativeZoom: 19,
    attribution: `${OSM_ATTRIBUTION}, Tiles cortesia do <a href="https://www.openstreetmap.cat" target="_blank">Breton OpenStreetMap Team</a>`,
  },
  [Tiles.OpenTopoMap]: {
    name: "OpenTopoMap",
    maxNativeZoom: 17,
    attribution: `Dados: ${OSM_ATTRIBUTION}, <a href="http://viewfinderpanoramas.org">SRTM</a> | Estilo: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)`,
  },
  [Tiles.ThunderforestOutdoors]: {
    name: "Thunderforest Outdoors",
    maxNativeZoom: 22,
    attribution: `&copy; <a href="http://www.thunderforest.com/">Thunderforest</a>, ${OSM_ATTRIBUTION}`,
  },
  [Tiles.ESRISatelite]: {
    name: "ESRI Satélite",
    maxNativeZoom: 28,
    attribution: `Tiles &copy; Esri &mdash; Fonte: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP e a comunidade de usuários GIS`,
  },
  [Tiles.ESRIFisico]: {
    name: "ESRI Físico",
    maxNativeZoom: 15,
    attribution: `${OSM_ATTRIBUTION} | Estilo: <a href="https://www.cyclosm.org/">CyclOSM</a>`,
  },
  [Tiles.ESRITopografico]: {
    name: "ESRI Topográfico",
    maxNativeZoom: 19,
    attribution: ESRI_ATTRIBUTION,
  },
  [Tiles.ESRIRelevo]: {
    name: "ESRI Relevo",
    maxNativeZoom: 13,
    attribution: ESRI_ATTRIBUTION,
  },
  [Tiles.ESRICinzaEscuro]: {
    name: "ESRI Cinza Escuro",
    maxNativeZoom: 16,
    attribution: ESRI_ATTRIBUTION,
  },
};

/** Mapas base oferecidos no seletor, na ordem de exibição. */
export const AVAILABLE_TILES: Tiles[] = [
  Tiles.OpenStreetMap,
  Tiles.OpenTopoMap,
  Tiles.OpenStreetMapCAT,
  Tiles.ESRISatelite,
  Tiles.ESRIFisico,
  Tiles.ESRITopografico,
  Tiles.ESRIRelevo,
  Tiles.ESRICinzaEscuro,
];

// Zoom/coluna/linha de uma tile centrada no Brasil, usada para gerar a
// miniatura de cada mapa base direto do serviço.
const PREVIEW_TILE = { z: 4, x: 5, y: 8 };

export function getTilePreviewUrl(url: string): string {
  return url
    .replace("{s}", "a")
    .replace("{r}", "")
    .replace("{z}", String(PREVIEW_TILE.z))
    .replace("{x}", String(PREVIEW_TILE.x))
    .replace("{y}", String(PREVIEW_TILE.y));
}
